import { TreeDataItem as BaseTreeDataItem } from "@/components/tree-view";
import { Calendar, LucideProps, Star } from "lucide-react";
import React from "react";
import { OutlookCalendarEvent } from "./useOutlookCalendarEventsList";

export type TreeDataItem = BaseTreeDataItem & {
  calendarType?: string;
  calendarId?: string;
};

export type CalendarTabConfig = {
  type: "calendar";
  calendarId: string;
  calendarName: string;
  icon: React.ReactNode;
};

export type TabConfig = CalendarTabConfig;

export const getCalendarIcon = (
  isPrimary?: boolean,
): React.ForwardRefExoticComponent<
  Omit<LucideProps, "ref"> & React.RefAttributes<SVGSVGElement>
> => {
  return isPrimary ? Star : Calendar;
};

// Event positioning types for day view
export interface PositionedEvent {
  event: {
    platform: string;
    base: string;
    type: string;
    data: {
      id: string;
      summary: string;
      start_date_time?: string;
      end_date_time?: string;
      start_time_zone?: string;
      end_time_zone?: string;
      location?: string;
      attendees?: Array<{
        id: string;
        email?: string;
        display_name?: string;
        response_status?: string;
      }>;
      status?: string;
      html_link?: string;
    };
  };
  isAllDay: boolean;
  startTime?: string;
  endTime?: string;
}

// Utility functions for calendar operations
export function getWeekDaysAround(selectedDate: Date) {
  const days = [];
  const today = new Date();

  for (let i = -3; i <= 3; i++) {
    const date = new Date(selectedDate);
    date.setDate(selectedDate.getDate() + i);

    days.push({
      date,
      dateKey: date.toISOString().split("T")[0],
      shortName: date.toLocaleDateString("en-US", {
        weekday: "short",
        day: "numeric",
      }),
      isSelected: date.toDateString() === selectedDate.toDateString(),
      isToday: date.toDateString() === today.toDateString(),
    });
  }

  return days;
}

export function getEventsForDay(events: any[], selectedDate: Date): any[] {
  const dayStart = new Date(selectedDate);
  dayStart.setHours(0, 0, 0, 0);

  const dayEnd = new Date(selectedDate);
  dayEnd.setHours(23, 59, 59, 999);

  return events.filter((event) => {
    if (!event.data?.start_date_time) return false;

    const eventStart = new Date(event.data.start_date_time);
    const eventEnd = event.data.end_date_time
      ? new Date(event.data.end_date_time)
      : eventStart;

    // Event overlaps with selected day
    return eventStart <= dayEnd && eventEnd >= dayStart;
  });
}

export function positionEventsInDay(events: any[]): any[] {
  return events.map((event) => {
    const startDateTime = event.data?.start_date_time;
    const endDateTime = event.data?.end_date_time;

    if (!startDateTime) {
      return { ...event, isAllDay: true };
    }

    const startDate = new Date(startDateTime);
    const endDate = endDateTime ? new Date(endDateTime) : startDate;

    // Check if it's an all-day event
    const isAllDay =
      startDate.getHours() === 0 &&
      startDate.getMinutes() === 0 &&
      endDate.getHours() === 0 &&
      endDate.getMinutes() === 0;

    return {
      ...event,
      isAllDay,
      startTime: isAllDay
        ? undefined
        : startDate.toLocaleTimeString("en-US", {
            hour: "numeric",
            minute: "2-digit",
          }),
      endTime: isAllDay
        ? undefined
        : endDate.toLocaleTimeString("en-US", {
            hour: "numeric",
            minute: "2-digit",
          }),
    };
  });
}

// Map Outlook calendar event to entity format
export function mapOutlookCalendarEventToEntity(
  event: OutlookCalendarEvent,
): PositionedEvent["event"] {
  return {
    platform: "outlook",
    base: "calendar_event",
    type: "outlook_calendar_event",
    data: {
      id: event.id,
      summary: event.subject,
      start_date_time: event.start?.dateTime,
      end_date_time: event.end?.dateTime,
      start_time_zone: event.start?.timeZone,
      end_time_zone: event.end?.timeZone,
      location: event.location?.displayName,
      attendees: event.attendees?.map((attendee) => ({
        id: attendee.emailAddress.address,
        email: attendee.emailAddress.address,
        display_name: attendee.emailAddress.name,
        response_status: attendee.status?.response,
      })),
      status: event.showAs,
      html_link: event.webLink,
    },
  };
}
